
import { insightsiqPlatformWorkIdMap } from "@entities/global-discovery/constants";
import { useAutoCompleteInfluencerSearch } from "@entities/global-discovery/constants/useAutoCompleteInfluencerSearch";
import { useGlobalDiscoveryFacets } from "@entities/global-discovery/hooks/useGlobalDiscoveryFacets";
import { useGetGlobalDiscoveryStore } from "@entities/global-discovery/store/useGetGlobalDiscoveryStoreV2";
import { IAutoCompleteInfluencer } from "@shared/lib/types/globalDiscovery";
import { Dropdown, Input, Select } from "@shared/ui";
import { Hash, Instagram, User, X, Youtube } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import styles from "./SearchBar.module.scss";

export function SearchBar({
  handleSaveFilters,
  handlePlatformChange,
}: {
  handleSaveFilters: () => void;
  handlePlatformChange: (platformId: string) => void;
}) {
  const { platformId, setPlatform, setSelectedFilter, selectedFilters } =
    useGetGlobalDiscoveryStore();

  const [inputValue, setInputValue] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Fetch autocomplete for Instagram
  const { autoCompleteInfluencers: igCreators, isFetching: isFetchingIg } =
    useAutoCompleteInfluencerSearch({
      text: debouncedQuery,
      work_platform_id: insightsiqPlatformWorkIdMap["instagram"],
    });

  // Fetch autocomplete for YouTube
  const { autoCompleteInfluencers: ytCreators, isFetching: isFetchingYt } =
    useAutoCompleteInfluencerSearch({
      text: debouncedQuery,
      work_platform_id: insightsiqPlatformWorkIdMap["youtube"],
    });

  // Fetch topic facets
  const { facet: topicFacets, isFetching: isFetchingTopics } =
    useGlobalDiscoveryFacets({
      facetName: "topics",
      body: {},
      query: debouncedQuery,
      work_platform_id: platformId,
      enabled: !!platformId && debouncedQuery.length > 0,
    });

  // Combine creators from both platforms
  const allCreators = useMemo(() => {
    const creators: Array<
      IAutoCompleteInfluencer & { platform: "instagram" | "youtube" }
    > = [];

    if (igCreators) {
      igCreators.forEach((creator) => {
        creators.push({
          ...creator,
          platform: "instagram",
        });
      });
    }

    if (ytCreators) {
      ytCreators.forEach((creator) => {
        creators.push({
          ...creator,
          platform: "youtube",
        });
      });
    }

    return creators.slice(0, 5); // Limit to 5 results
  }, [igCreators, ytCreators]);

  // Filter topics based on search query
  const matchingTopics = useMemo(() => {
    const selectedTopicValues = Array.isArray(selectedFilters.topics)
      ? (selectedFilters.topics as string[])
      : [];
    const selectedTopicLabels = Array.isArray(
      selectedFilters.filterTags?.topics
    )
      ? (selectedFilters.filterTags.topics as string[])
      : [];

    const selectedTopicMap = new Map<string, string>();
    selectedTopicValues.forEach((value, idx) => {
      selectedTopicMap.set(value, selectedTopicLabels[idx] || value);
    });

    // If no facets are available, fall back to selected topics
    if (!topicFacets) {
      return selectedTopicValues.map((value) => ({
        value,
        name: selectedTopicMap.get(value) || value,
      }));
    }

    if (!debouncedQuery) {
      const baseTopics = topicFacets.slice(0, 6);
      const baseValues = new Set(baseTopics.map((t) => t.value));

      const selectedButMissing = selectedTopicValues
        .filter((value) => !baseValues.has(value))
        .map((value) => ({
          value,
          name: selectedTopicMap.get(value) || value,
        }));

      return [...selectedButMissing, ...baseTopics];
    }

    return topicFacets
      .filter((topic) =>
        topic.name.toLowerCase().includes(debouncedQuery.toLowerCase())
      )
      .slice(0, 6);
  }, [topicFacets, debouncedQuery, selectedFilters]);

  const isFetching = isFetchingIg || isFetchingYt || isFetchingTopics;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    setIsDropdownOpen(true);
    // Don't call focus() - let the input maintain natural focus
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  const handleClearInput = () => {
    setInputValue("");
    // Focus will be maintained automatically by the input
  };

  const handleUsernameSearch = (
    username: string,
    platformToSet: "instagram" | "youtube"
  ) => {
    const platformIdToSet =
      platformToSet === "instagram"
        ? insightsiqPlatformWorkIdMap["instagram"]
        : insightsiqPlatformWorkIdMap["youtube"];

    const isPlatformChanged = platformIdToSet !== platformId;

    if (isPlatformChanged) {
      setPlatform(platformIdToSet);
    }

    setSelectedFilter((prevFilters) => {
      const currentSort = (prevFilters as any)?.sort_by;

      // if (isPlatformChanged) {
      return {
        username: username,
        sort_by: currentSort,
        filterTags: {
          username: username,
        },
      };
    });

    handleSaveFilters();
    setInputValue("");
    setIsDropdownOpen(false);
  };

  const handleCreatorSelect = (
    creator: IAutoCompleteInfluencer & { platform: "instagram" | "youtube" }
  ) => {
    handleUsernameSearch(creator.username, creator.platform);
  };

  const handleTopicSelect = (topicValue: string, topicName: string) => {
    setSelectedFilter((prevFilters) => {
      const currentTopics = Array.isArray(prevFilters.topics)
        ? (prevFilters.topics as string[])
        : [];
      const currentLabels = Array.isArray(prevFilters.filterTags?.topics)
        ? (prevFilters.filterTags.topics as string[])
        : [];

      const isSelected = currentTopics.includes(topicValue);

      if (isSelected) {
        // Deselect: remove the topic
        const topicIndex = currentTopics.indexOf(topicValue);
        return {
          ...prevFilters,
          topics: currentTopics.filter((_, idx) => idx !== topicIndex),
          filterTags: {
            ...(prevFilters.filterTags || {}),
            topics: currentLabels.filter((_, idx) => idx !== topicIndex),
          },
        };
      } else {
        // Select: add the topic
        return {
          ...prevFilters,
          topics: [...currentTopics, topicValue],
          filterTags: {
            ...(prevFilters.filterTags || {}),
            topics: [...currentLabels, topicName],
          },
        };
      }
    });
  };

  const getPlatformIcon = (platform: "instagram" | "youtube") => {
    if (platform === "instagram") {
      return <Instagram size={18} className={styles.instagramIcon} />;
    }
    return <Youtube size={18} className={styles.youtubeIcon} />;
  };

  // Platform options
  const platformOptions = useMemo(
    () => [
      {
        label: "Instagram",
        value: insightsiqPlatformWorkIdMap["instagram"],
        icon: <Instagram size={20} className={styles.instagramIcon} />,
      },
      {
        label: "YouTube",
        value: insightsiqPlatformWorkIdMap["youtube"],
        icon: <Youtube size={20} className={styles.youtubeIcon} />,
      },
    ],
    []
  );

  const handlePlatformSelect = (value: string) => {
    handlePlatformChange(value);
  };

  const hasResults =
    allCreators.length > 0 ||
    matchingTopics.length > 0 ||
    debouncedQuery.length > 0;

  return (
    <div className={styles.searchBar}>
      <div className={styles.searchWrapper}>
        {/* Platform Selector with Divider */}
        <div className={styles.platformSelector}>
          <Select
            value={platformId}
            onChange={handlePlatformSelect}
            options={platformOptions.map((opt) => ({
              label: (
                <div className={styles.platformOptionLabel}>
                  {opt.icon}
                  <span>{opt.label}</span>
                </div>
              ),
              value: opt.value,
            }))}
            className={styles.platformSelect}
            dropdownMatchSelectWidth={false}
            getPopupContainer={(trigger) =>
              trigger.parentElement || document.body
            }
          />
          {/* Divider */}
          <div className={styles.divider} />
        </div>

        {/* Search Input */}
        <div className={styles.inputWrapper}>
          <Dropdown
            open={isDropdownOpen && hasResults}
            onOpenChange={setIsDropdownOpen}
            trigger={["click"]}
            placement="bottomLeft"
            // menu={{ items: [] }}
            // overlayClassName={styles.dropdownOverlay}
            // getPopupContainer={(trigger) =>
            //   trigger.parentElement || document.body
            // }
            dropdownRender={() => (
              <div className={styles.dropdown}>
                {/* Search by Username Section */}
                {debouncedQuery.trim().length > 0 && (
                  <div>
                    <div className={styles.sectionHeader}>
                      <User size={12} />
                      <span>Search by Username</span>
                    </div>
                    <div className={styles.sectionContent}>
                      <button
                        type="button"
                        onClick={() =>
                          handleUsernameSearch(debouncedQuery, "instagram")
                        }
                        className={`${styles.platformSearchButton} ${styles.instagramButton}`}
                      >
                        <div className={styles.platformIconWrapper}>
                          <Instagram
                            size={14}
                            className={styles.instagramIcon}
                          />
                        </div>
                        <div>
                          <div className={styles.platformSearchTitle}>
                            Search "{debouncedQuery}" on Instagram
                          </div>
                          <div className={styles.platformSearchSubtitle}>
                            View profile
                          </div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleUsernameSearch(debouncedQuery, "youtube")
                        }
                        className={`${styles.platformSearchButton} ${styles.youtubeButton}`}
                      >
                        <div className={styles.platformIconWrapper}>
                          <Youtube size={14} className={styles.youtubeIcon} />
                        </div>
                        <div>
                          <div className={styles.platformSearchTitle}>
                            Search "{debouncedQuery}" on YouTube
                          </div>
                          <div className={styles.platformSearchSubtitle}>
                            View channel
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                {/* Topics Section */}
                <div>
                  <div className={styles.sectionHeader}>
                    <Hash size={12} />
                    <span>
                      {debouncedQuery ? "Matching Topics" : "Popular Topics"}
                    </span>
                  </div>
                  <div className={styles.sectionContent}>
                    {matchingTopics.length > 0 ? (
                      matchingTopics.map((topic) => {
                        const isSelected = Array.isArray(selectedFilters.topics)
                          ? (selectedFilters.topics as string[]).includes(
                              topic.value
                            )
                          : false;

                        return (
                          <button
                            key={topic.value}
                            type="button"
                            onClick={() =>
                              handleTopicSelect(topic.value, topic.name)
                            }
                            className={`${styles.topicButton} ${
                              isSelected ? styles.selected : ""
                            }`}
                          >
                            <div
                              className={`${styles.topicIcon} ${
                                isSelected ? styles.selectedIcon : ""
                              }`}
                            >
                              <Hash size={14} />
                            </div>
                            <div
                              className={`${styles.topicName} ${
                                isSelected ? styles.selectedName : ""
                              }`}
                            >
                              {topic.name}
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      <div className={styles.emptyState}>
                        <p>No matching topics found.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Matching Creators Section */}
                {debouncedQuery.trim().length > 0 && allCreators.length > 0 && (
                  <div>
                    <div className={styles.sectionHeader}>
                      <User size={12} />
                      <span>Matching Creators</span>
                    </div>
                    <div className={styles.sectionContent}>
                      {allCreators.map((creator) => (
                        <button
                          key={`${creator.platform}-${creator.user_id}`}
                          type="button"
                          onClick={() => handleCreatorSelect(creator)}
                          className={styles.creatorButton}
                        >
                          <img
                            src={creator.picture}
                            alt={creator.fullname}
                            className={styles.creatorImage}
                          />
                          <div className={styles.creatorInfo}>
                            <div className={styles.creatorName}>
                              {creator.fullname}
                            </div>
                            <div className={styles.creatorHandle}>
                              @{creator.username}
                            </div>
                          </div>
                          <div className={styles.creatorPlatform}>
                            {getPlatformIcon(creator.platform)}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Loading State */}
                {isFetching && (
                  <div className={styles.loadingState}>
                    <p>Searching...</p>
                  </div>
                )}
              </div>
            )}
          >
            <Input
              id="search"
              placeholder="Search creators, categories, or usernames..."
              className={styles.input}
              variant="borderless"
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              suffix={
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearInput();
                  }}
                  className={styles.clearButton}
                >
                  <X size={14} />
                </button>
              }
            />
          </Dropdown>
        </div>
      </div>
    </div>
  );
}
